/**
 * Pure helpers for call join/leave sounds: diff participant presence between
 * renders and pick which configured sound (if any) applies.
 */

import type { NotificationSoundId } from '../constants/notificationSoundPreferenceShared';
import type { CallPresenceSoundPrefs } from '../hooks/callPresenceSoundStorage';

/** The call or voice channel this device is in, with everyone currently present. */
export interface CallPresenceState {
  /** `call.id` for conversation calls, `spaceId:channelId` for voice channels. */
  key: string;
  activeIds: ReadonlySet<string>;
}

export interface CallPresenceDiff {
  selfJoined: boolean;
  selfLeft: boolean;
  /** Other participants who appeared; never includes `myId`. */
  joined: string[];
  /** Other participants who disappeared; never includes `myId`. */
  left: string[];
}

export interface CallPresenceSoundChoice {
  soundId: NotificationSoundId;
  customPath: string | null;
  volume: number;
}

export function activeParticipantIds(
  participants: readonly { identityId: string; leftAt?: string }[]
): Set<string> {
  const ids = new Set<string>();
  for (const p of participants) {
    if (!p.leftAt) ids.add(p.identityId);
  }
  return ids;
}

/**
 * Entering a call (or switching to another one) counts as a self-join and
 * baselines everyone already there; leaving counts as a self-leave.
 */
export function diffCallPresence(
  prev: CallPresenceState | null,
  next: CallPresenceState | null,
  myId: string | null
): CallPresenceDiff {
  const diff: CallPresenceDiff = { selfJoined: false, selfLeft: false, joined: [], left: [] };
  if (!prev && !next) return diff;
  if (!prev) {
    diff.selfJoined = true;
    return diff;
  }
  if (!next) {
    diff.selfLeft = true;
    return diff;
  }
  if (prev.key !== next.key) {
    diff.selfJoined = true;
    return diff;
  }
  for (const id of next.activeIds) {
    if (id !== myId && !prev.activeIds.has(id)) diff.joined.push(id);
  }
  for (const id of prev.activeIds) {
    if (id !== myId && !next.activeIds.has(id)) diff.left.push(id);
  }
  return diff;
}

export function resolveCallPresenceSound(
  prefs: CallPresenceSoundPrefs,
  who: { isSelf: boolean; isFriend: boolean }
): CallPresenceSoundChoice | null {
  if (!prefs.enabled) return null;
  if (who.isSelf && !prefs.includeSelf) return null;
  const choice: CallPresenceSoundChoice =
    !who.isSelf && who.isFriend && prefs.friendEnabled
      ? { soundId: prefs.friendSoundId, customPath: prefs.friendCustomPath, volume: prefs.friendVolume }
      : { soundId: prefs.soundId, customPath: prefs.customPath, volume: prefs.volume };
  if (choice.soundId === 'none' || choice.volume <= 0) return null;
  if (choice.soundId === 'custom' && !choice.customPath) return null;
  return choice;
}

/** One sound for a batch of others joining or leaving together; any friend wins the friend sound. */
export function resolveCallPresenceBatchSound(
  prefs: CallPresenceSoundPrefs,
  identityIds: readonly string[],
  friendIds: ReadonlySet<string>
): CallPresenceSoundChoice | null {
  if (identityIds.length === 0) return null;
  const isFriend = identityIds.some((id) => friendIds.has(id));
  return resolveCallPresenceSound(prefs, { isSelf: false, isFriend });
}
